import { css } from '@emotion/core';
import { Avatar as MaterialAvatar, Theme, Typography, useTheme } from '@material-ui/core';
import * as React from 'react';

import { DateValue, Education, Position, Profile } from 'shared/profile-search/profile';
import { ProfileDataSourceProvider } from '../../hooks/use-profile-data-source';
import { ProfileAge } from '../../sfc/profile-age';
import { CompanyInfo } from '../company-info';

const styles = (theme: Theme) => css`
    position: relative;
    flex: 1 1 auto;
    padding-right: 125px;

    .profile-age {
        position: absolute;
        right: 0;
        bottom: 0;
    }

    .text-separator {
        &:before {
            content: '·';
            padding: 0 2px 0 4px;
            font-weight: 900;
        }
    }

    .secondary-text {
        color: rgba(0, 0, 0, 0.42);
    }

    .section {
        display: flex;
        margin-bottom: 15px;
        line-height: 22px;

        &:last-child {
            margin-bottom: 0;
        }

        .section-icon {
            flex: 0 0 auto;
            width: 80px;
            text-align: right;
            color: ${theme.palette.text.secondary};
        }

        .section-details {
            flex: 1 1 auto;
            padding-left: 20px;
            color: ${theme.palette.text.primary};
        }
    }

    .cursor {
        cursor: pointer;
    }
`;

const avatarSize = 60;
const maxPreviewItems = 5;

const extractYear = (date: DateValue | null): string | null => {
    if (!date?.value) return null;
    return date.value.split('-')[0];
};

const formatDateRange = (start: DateValue | null, end: DateValue | null, current?: boolean): JSX.Element | null => {
    const startYear = extractYear(start);
    if (!startYear) return null;
    const endStr = current ? 'Present' : (extractYear(end) ?? 'Present');
    return (
        <span className="secondary-text">
            {startYear} - {endStr}
        </span>
    );
};

const DetailsWithSeparator: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const validChildren = React.Children.toArray(children).filter((child) => child);
    const items = validChildren.map((child, index) => {
        const separator = index < validChildren.length - 1 ? <span className="text-separator" /> : null;
        return (
            <React.Fragment key={index}>
                {child}
                {separator}
            </React.Fragment>
        );
    });
    return <div>{items}</div>;
};

const PositionItem: React.FC<{ position: Position }> = ({ position }) => {
    const companyData = { companyName: position.company, companyUrl: position.companyUrl };
    return (
        <DetailsWithSeparator>
            <span>{position.title}</span>
            <CompanyInfo data={companyData} />
            {formatDateRange(position.startDate, position.endDate, position.current)}
        </DetailsWithSeparator>
    );
};

const EducationItem: React.FC<{ education: Education }> = ({ education }) => {
    const degree = education.degree || '';
    const field = education.field || '';
    const degreeField = degree && field ? `${degree}, ${field}` : degree || field || null;
    return (
        <DetailsWithSeparator>
            {degreeField ? <span>{degreeField}</span> : null}
            <span>{education.school}</span>
            {formatDateRange(education.startDate, education.endDate)}
        </DetailsWithSeparator>
    );
};

export interface ProfilePreviewProps {
    onClick?: () => void;
    profile: Profile;
}

export const ProfilePreview: React.FC<ProfilePreviewProps> = ({ onClick, profile }) => {
    const theme = useTheme();

    const initials = (profile.firstName?.charAt(0) ?? '') + (profile.lastName?.charAt(0) ?? '');
    const avatarStyle = { flex: '0 0 auto', fontSize: avatarSize / 2, height: avatarSize, width: avatarSize };

    const currentPositions = profile.positions.filter((p) => p.current).slice(0, maxPreviewItems);
    const pastPositions = profile.positions.filter((p) => !p.current).slice(0, maxPreviewItems);
    const educations = profile.educations.slice(0, maxPreviewItems);

    const currentPositionItems = currentPositions.map((p, i) => <PositionItem key={i} position={p} />);
    const pastPositionItems = pastPositions.map((p, i) => <PositionItem key={i} position={p} />);
    const educationItems = educations.map((e, i) => <EducationItem key={i} education={e} />);

    const currentSection =
        currentPositions.length > 0 ? (
            <div className="section">
                <div className="section-icon">Current</div>
                <div className="section-details">{currentPositionItems}</div>
            </div>
        ) : null;

    const pastSection =
        pastPositions.length > 0 ? (
            <div className="section">
                <div className="section-icon">Past</div>
                <div className="section-details">{pastPositionItems}</div>
            </div>
        ) : null;

    const educationSection =
        educations.length > 0 ? (
            <div className="section">
                <div className="section-icon">Education</div>
                <div className="section-details">{educationItems}</div>
            </div>
        ) : null;

    const profileAgeChip = profile.viewedAt ? (
        <div className="profile-age">
            <ProfileDataSourceProvider personId={profile.personId}>
                <ProfileAge viewedAt={profile.viewedAt} />
            </ProfileDataSourceProvider>
        </div>
    ) : null;

    return (
        <div css={styles(theme)}>
            {profileAgeChip}
            <div className="section">
                <div className="section-icon cursor" onClick={onClick}>
                    <MaterialAvatar className="avatar" style={avatarStyle}>
                        {initials.toUpperCase()}
                    </MaterialAvatar>
                </div>
                <div className="section-details cursor" onClick={onClick}>
                    <Typography variant="h4" component="div">
                        {profile.fullName}
                    </Typography>
                    <div>{profile.headline}</div>
                    <div>{profile.location}</div>
                </div>
            </div>
            {currentSection}
            {pastSection}
            {educationSection}
        </div>
    );
};
